import { type StatusType } from '@atrvd/saas-ui'
import { formatDateTime } from '@dcp/shared/utils/formatters'
import {
  isLedgerSyncConnectionStatus,
  type LedgerSyncConnectionStatus,
} from '@dcp/shared/ledgersync/connection-status'
import type { BankConnectionRow } from '@/lib/db-tenant'
import { connectionAttention, type ConnectionAttention } from '@/lib/ledgersync-connection-attention'

/**
 * Map a v3 connection status to a StatusBadge variant. Keyed on the shared
 * vocabulary so a new status fails to compile here; a value unknown at runtime
 * gets a `warning` badge in {@link presentConnectionStatus}.
 *
 * `requires_action` is a warning because on a connection that already owns
 * accounts it means the bank stopped delivering until the user acts. The
 * in-progress connect case is softened in {@link presentConnectionStatus}.
 */
const STATUS_BADGE: Record<LedgerSyncConnectionStatus, StatusType> = {
  active: 'success',
  failed: 'error',
  disconnected: 'warning',
  requires_action: 'warning',
  initiated: 'info',
}

/** Human-readable labels for the v3 connection statuses shown in the badge. */
const STATUS_LABEL: Record<LedgerSyncConnectionStatus, string> = {
  active: 'Connected',
  failed: 'Failed',
  disconnected: 'Disconnected',
  requires_action: 'Action required',
  initiated: 'Connecting…',
}

/**
 * Why each unhealthy status needs the user, shown when the vendor sent no
 * `last_error`. `unknown` covers a status newer than this bundle.
 */
const ATTENTION_FALLBACK: Record<ConnectionAttention, string> = {
  unknown: 'The connection needs attention.',
  requires_action: 'The institution needs you to sign in again or confirm access.',
  failed: 'The connection stopped working.',
  disconnected: 'Access to this institution was revoked.',
}

/**
 * Points the user at the one fix for an unhealthy connection (ADR 0011:
 * reauthorize). For the connection list and detail header, which sit next to
 * the actions menu.
 */
export const RECONNECT_HINT = 'Use Reconnect in the actions menu to restore it.'

/** `text` ending in sentence punctuation, adding a full stop when it has none. */
function withFullStop(text: string): string {
  const trimmed = text.trim()
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

/** The same instruction for pages away from the connection, naming where Reconnect lives. */
export const RECONNECT_ELSEWHERE_HINT = 'Reconnect the bank in Settings → Financial Accounts.'

/** How a connection's status is drawn: badge variant, label and the explanation behind it. */
export interface ConnectionStatusPresentation {
  badge: StatusType
  label: string
  /**
   * True when an EXISTING connection stopped delivering data and needs the
   * user (`requires_action`, `failed`, `disconnected`).
   */
  unhealthy: boolean
  /** Tooltip text: the reason plus the Reconnect hint; null when healthy. */
  attention: string | null
}

/**
 * Whether a connection is still in its first connect: `requires_action` with no
 * account yet, i.e. the user is in the hosted widget. The same `requires_action`
 * on a connection that owns accounts is a broken connection instead.
 *
 * Shared by {@link presentConnectionStatus} (neutral badge) and the connection
 * list's status poll (only an in-progress connect is polled), so the two
 * cannot drift.
 *
 * Known false positive: `accounts_count` counts every account with
 * `deleted_at IS NULL` (disabled and provider-removed ones included), so it is
 * zero for an existing connection only when all its accounts were deleted. A
 * `requires_action` connection in that state reads as connecting; it has no
 * live data to be stale, so nothing is hidden.
 *
 * @param connection - status and live account count
 * @returns true for an in-progress first connect
 */
export function isFirstConnectInProgress(
  connection: Pick<BankConnectionRow, 'status' | 'accounts_count'>,
): boolean {
  return connection.status === 'requires_action' && connection.accounts_count === 0
}

/**
 * Decide how a connection's status is presented in the list and the detail
 * header.
 *
 * `requires_action` has two meanings. During a first connect
 * ({@link isFirstConnectInProgress}) it is the normal state of the flow and
 * stays a neutral pending badge with no warning. Once the connection owns
 * accounts it means their data is frozen until the user reconnects, which is
 * the same kind of problem as `failed`/`disconnected`.
 *
 * Whether a status is unhealthy is decided by `connectionAttention`, the rule
 * every surface shares; an unknown status is unhealthy and shows its raw text.
 *
 * @param connection - status, live account count and the vendor's last error
 * @returns badge variant, label, health flag and tooltip text
 */
export function presentConnectionStatus(
  connection: Pick<BankConnectionRow, 'status' | 'accounts_count' | 'last_error'>,
): ConnectionStatusPresentation {
  const { status } = connection
  const attention = connectionAttention(status)
  // A vendor message often lacks closing punctuation; end it before the hint.
  const explain = (reason: string) => `${withFullStop(connection.last_error ?? reason)} ${RECONNECT_HINT}`
  // The column is plain TEXT: a status newer than this bundle has no label or
  // badge of its own, shows its raw string, and — as `connectionAttention`
  // classifies it `'unknown'` — always needs attention.
  if (!isLedgerSyncConnectionStatus(status)) {
    return {
      badge: 'warning',
      label: status,
      unhealthy: true,
      attention: explain(ATTENTION_FALLBACK.unknown),
    }
  }
  const presented = { badge: STATUS_BADGE[status], label: STATUS_LABEL[status] }
  if (isFirstConnectInProgress(connection)) {
    return { ...presented, badge: 'pending', unhealthy: false, attention: null }
  }
  if (!attention) return { ...presented, unhealthy: false, attention: null }
  return { ...presented, unhealthy: true, attention: explain(ATTENTION_FALLBACK[attention]) }
}

/**
 * The vendor's freshness line — when the BANK last delivered data, as opposed
 * to `last_sync_at`, which is when we last fetched it. It says how old the
 * bank's figures are, which matters exactly when the two diverge.
 *
 * @param dataRefreshedAt - the connection's `data_refreshed_at`
 * @returns "Bank data as of …", or null when the vendor has reported no refresh
 */
export function bankDataAsOfLabel(dataRefreshedAt: string | null): string | null {
  return dataRefreshedAt ? `Bank data as of ${formatDateTime(dataRefreshedAt)}` : null
}

/**
 * The capabilities surfaced as badges, in display order. The v3 capability
 * snapshot can carry more keys than these, but only the ones meaningful to the
 * banking UI are rendered to keep the row compact.
 */
export const CAPABILITY_ORDER = ['transactions', 'balance', 'statements'] as const
export type CapabilityKey = (typeof CAPABILITY_ORDER)[number]

/** Short labels for the capability badges. */
export const CAPABILITY_LABEL: Record<CapabilityKey, string> = {
  transactions: 'Transactions',
  balance: 'Balance',
  statements: 'Statements',
}

/**
 * The one capability status that must NOT be rendered as a chip.
 *
 * Capability chips are all one flat grey (see {@link CapabilityBadges}), so an
 * `unsupported` capability drawn as a chip would be indistinguishable from a
 * working one and would claim the bank offers something it does not.
 */
export const UNSUPPORTED_CAPABILITY_STATUS = 'unsupported'

/**
 * Read a single capability's status from the loose v3 `realized_capabilities`
 * JSON. The shape is `{ <capability>: { status: string } }` but the column is
 * typed `Record<string, unknown>`, so every access is guarded — a malformed or
 * partial object yields `null` (badge skipped) rather than throwing.
 *
 * @param capabilities - The connection's realized_capabilities JSON (or null).
 * @param key - Which capability to read.
 * @returns The status string, or null when absent / malformed.
 */
export function readCapabilityStatus(
  capabilities: Record<string, unknown> | null,
  key: CapabilityKey,
): string | null {
  if (!capabilities || typeof capabilities !== 'object') return null
  const entry = (capabilities as Record<string, unknown>)[key]
  if (!entry || typeof entry !== 'object') return null
  const status = (entry as Record<string, unknown>).status
  return typeof status === 'string' ? status : null
}
